import hoistStatics from 'hoist-non-react-statics';

/**
 * Creates a display name to use when hoisting hoc statics, derived from a
 * component that is going to be wrapped by the higher-order component (hoc).
 * Will check for a `displayName` or `name` prop on the React component, or will fall through to
 * a default value if neither exists on the component.
 *
 * @example
 * getDisplayName()
 * // () => 'Component'
 * getDisplayName({ prop1: 1, prop2: 'lorem' }, 'SomeComponent')
 * // () => 'SomeComponent'
 * getDisplayName({ name: 'john', prop2: 'lorem' }, 'SomeComponent')
 * // () => 'john'
 * getDisplayName({ name: 'john', displayName: 'JohnComponent' }, 'SomeComponent')
 * // () => 'JohnComponent'
 * @kind function
 * @name getDisplayName
 * @sig Object -> String -> String
 * @param {Element} Component A React component that may or may not have a `displayName` or `name` prop on it already
 * @param {String} defaultDisplayName A fallback value to use if the component doesn't contain a prop that can be used instead (defaults to just "Component")
 * @returns {String} A string value that will be the display name for the React component
 */
function getDisplayName({ displayName, name } = {}, defaultDisplayName = 'Component') {
  return displayName || name || defaultDisplayName;
}

/**
 * Hoists (some) static props from a component onto another component that is
 * designed to wrap around it. This is necessary for static props like `displayName`
 *
 * @kind function
 * @name withStatics
 * @sig Component -> Component -> Component
 * @param {Element} EnhancerComponent A React component that will wrap around the other one
 * @param {Element} WrappedComponent A React component that will be wrapped
 * @returns {Element} A React Component that is simply the wrapping of the second component with the first component, but with certain static props hoisted from the inner component onto the outer component
 */
function withStatics(EnhancerComponent, WrappedComponent) {
  EnhancerComponent.displayName = `${getDisplayName(EnhancerComponent, 'EnhancerComponent')}(${
    WrappedComponent.wrapperDisplayName || getDisplayName(WrappedComponent, 'WrappedComponent')
  })`;
  EnhancerComponent.WrappedComponent = WrappedComponent;
  return hoistStatics(EnhancerComponent, WrappedComponent);
}

/**
 * Takes a value that might be a String or an Array of Strings and will return
 * an Array of non-blank, lowercase Strings if it is NOT empty,
 * otherwise will return a fallback value.
 *
 * @example
 * whitelistOrDefault('Lorem')
 * () => ['lorem']
 * whitelistOrDefault(['lorem', 'ipsum', 'dolor', 'SIT', 'amet'])
 * () => ['lorem', 'ipsum', 'dolor', 'sit', 'amet']
 * whitelistOrDefault([1, 2, 3, 4])
 * () => []
 * whitelistOrDefault(null, ['lorem', 'ipsum'])
 * () => ['lorem', 'ipsum']
 * @kind function
 * @name whitelistOrDefault
 * @sig String|String[] -> String[]
 * @param {String|String[]} val A string OR an Array of Strings
 * @param {String[]} fallback A fallback Array of Strings, in case the provided value is empty
 * @returns {String[]} An array of string values
 */
function whitelistOrDefault(val, fallback = []) {
  const newArr = (Array.isArray(val) ? val : [val]);
  return (newArr.length ? newArr : fallback)
    .filter(v => typeof v === 'string' && v)
    .map(n => n.toLowerCase());
}

/**
 * Invokes the analytics tracker's trackConversion() or trackEvent() for
 * conversion or standard events (respectively).
 * This is performed for every one of the analytics configs.
 *
 * @kind function
 * @name track
 * @param {String} type The analytics event type (either "event" or "conversion")
 * @param {String} eventName A custom name, which will be used in the remote analtyics tracking tool to uniquely identify the event
 * @param {Object} allEventData An object containing event data meant for each of the analytics clients. Each key should match the name of on of the analytics clients (ie, "kissmetrics", "kinesis", "google", etc)
 * @param {Object[]} analyticsClients A collection of analytics clients
 * @param {String[]} onlyToTheseClients An optional list of analytics clients (by name) that this particular event should go to (the reset will be skipped for this event)
 */
function track(type, eventName, allEventData = {}, analyticsClients = [], onlyToTheseClients = []) {
  const clientsToSendTo = whitelistOrDefault(onlyToTheseClients, analyticsClients.map(n => n.name));

  const promises = [];

  analyticsClients
    .filter(({ name }) => clientsToSendTo.includes(name))
    .forEach(({ name, tracker = {} }) => {
      const { trackEvent, trackConversion, trackUser } = tracker;
      const eventData = allEventData[name];
      if (type === 'conversion' && eventData && typeof trackConversion === 'function') {
        trackConversion(eventName, eventData);
      } else if (type === 'identity' && typeof trackUser === 'function') {
        trackUser(eventName, eventData);
      } else if (type === 'event' && typeof trackEvent === 'function') {
        trackEvent(eventName, eventData);
      } else if (type === 'eventAsync' && typeof trackEvent === 'function') {
        promises.push(trackEvent(eventName, eventData));
      }
    });

  return Promise.all(promises);
}

export { getDisplayName, track, withStatics };
